import React, { useCallback, useEffect, useRef, useState } from "react";

type ArtworkImageBase = {
  src: string;
  alt: string;
  loading?: "lazy" | "eager";
  imgClassName?: string;
};

type FixedAspectProps = ArtworkImageBase & {
  aspectClass: string;
  objectFit?: "cover" | "contain";
  containerClassName?: string;
};

type MasonryProps = ArtworkImageBase & {
  layout: "masonry";
  containerClassName?: string;
};

type FlexibleProps = ArtworkImageBase & {
  layout: "flexible";
  minHeightClass?: string;
  maxHeightClass?: string;
  containerClassName?: string;
};

export type ArtworkImageProps = FixedAspectProps | MasonryProps | FlexibleProps;

function ImageSkeleton() {
  return (
    <div
      className="art-image-skeleton absolute inset-0"
      aria-hidden
    />
  );
}

function imageMatchesSrc(img: HTMLImageElement, src: string): boolean {
  if (img.getAttribute("src") === src) return true;
  try {
    const resolved = new URL(src, window.location.origin).href;
    return img.src === resolved;
  } catch {
    return false;
  }
}

export function ArtworkImage(props: ArtworkImageProps) {
  const { src, alt, loading = "lazy", imgClassName = "" } = props;
  const isMasonry = "layout" in props && props.layout === "masonry";

  const [loaded, setLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const activeSrcRef = useRef(src);

  const markLoaded = useCallback(
    (img: HTMLImageElement) => {
      if (!imageMatchesSrc(img, activeSrcRef.current)) return;
      if (isMasonry && img.naturalWidth > 0 && img.naturalHeight > 0) {
        const nextRatio = img.naturalWidth / img.naturalHeight;
        setAspectRatio((prev) => (prev === nextRatio ? prev : nextRatio));
      }
      setLoaded(true);
    },
    [isMasonry],
  );

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      markLoaded(event.currentTarget);
    },
    [markLoaded],
  );

  useEffect(() => {
    activeSrcRef.current = src;
    setLoaded(false);
    setAspectRatio(null);
  }, [src]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !imageMatchesSrc(img, src)) return;
    if (img.complete && img.naturalWidth > 0) {
      markLoaded(img);
    }
  }, [src, markLoaded]);

  if (isMasonry) {
    const { containerClassName = "" } = props as MasonryProps;
    return (
      <div
        className={`relative w-full overflow-hidden bg-canvas-100 ${
          aspectRatio ? "" : "aspect-[3/4]"
        } ${containerClassName}`}
        style={aspectRatio ? { aspectRatio } : undefined}
      >
        {!loaded && <ImageSkeleton />}
        <img
          key={src}
          ref={imgRef}
          src={src}
          alt={alt}
          loading={loading}
          onLoad={handleLoad}
          className={`img-baked absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
        />
      </div>
    );
  }

  if ("layout" in props && props.layout === "flexible") {
    const {
      containerClassName = "",
      minHeightClass = "min-h-[min(40vh,20rem)]",
      maxHeightClass = "max-h-[min(52vh,28rem)]",
    } = props as FlexibleProps;
    return (
      <div
        className={`relative flex w-full items-center justify-center bg-canvas-100 ${minHeightClass} ${containerClassName}`}
      >
        {!loaded && <ImageSkeleton />}
        <img
          key={src}
          ref={imgRef}
          src={src}
          alt={alt}
          loading={loading}
          onLoad={handleLoad}
          className={`img-baked w-full object-contain transition-opacity duration-300 ${maxHeightClass} ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
        />
      </div>
    );
  }

  const { aspectClass, objectFit = "cover", containerClassName = "" } =
    props as FixedAspectProps;

  return (
    <div
      className={`relative w-full overflow-hidden bg-canvas-100 ${aspectClass} ${containerClassName}`}
    >
      {!loaded && <ImageSkeleton />}
      <img
        key={src}
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        onLoad={handleLoad}
        className={`img-baked absolute inset-0 h-full w-full transition-opacity duration-300 ${
          objectFit === "contain" ? "object-contain" : "object-cover"
        } ${loaded ? "opacity-100" : "opacity-0"} ${imgClassName}`}
      />
    </div>
  );
}
